import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  useToast
} from '@chakra-ui/react';
import Form from '@rjsf/chakra-ui';
import validator from "@rjsf/validator-ajv8";
import { getDeletedValues } from '../../../util/helper';
import { getLocationData, updateLocationData, getLocationAliasSuggestions } from '../../../services/Location';
import FormLoader from '../../../CommonComponents/FormLoader';
import TypeaheadWidget from '../../../CommonComponents/TypeaheadWidget';
import { showErrorToast, showSuccessToast } from "../../../util/errorHandler";


const ArchitecturalLandmarks = () => {
  const { alias } = useParams();
  const [basicFieldData, setBasicFieldData] = useState({});
  const [processedData, setProcessedData] = useState({});
  const [quickSave, setQuickSave] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toast = useToast();

  const schema = {
    "title": "",
    "description": "",
    "type": "object",
    "properties": {
      "architectural_landmarks": {
        "type": "array",
        "title": "Architectural Landmarks",
        "items": {
          "type": "object",
          "properties": {
            "alias": {
              "type": "string",
              "title": "Alias"
            },
          }
        }
      },
    }
  }

  const fetchLocationAliasSuggestions = async (inputValue) => {
    const fetchedLocationAliasSuggestions = await loadLocationAliasSuggestions(inputValue);

    // Ensure inputValue is string and trimmed
    if (!inputValue || typeof inputValue !== "string" || !fetchedLocationAliasSuggestions) {
      return [];
    }

    return fetchedLocationAliasSuggestions?.map((item) => ({
      label: item.alias,
      value: item.alias
    }));
  };

  const loadLocationAliasSuggestions = async (inputValue) => {
    try {
      const result = await getLocationAliasSuggestions(20, 1, inputValue);
      return result?.data?.data || [];
    } catch (error) {
            showErrorToast(toast, "Failed to load location suggestions");
      return [];
    }
  };

  const widgets = {
    typeahead: (props) => (
      <TypeaheadWidget {...props} fetchOptions={fetchLocationAliasSuggestions} />
    ),
  };

  const uiSchema = {
    "ui:submitButtonOptions": {
      "norender": true
    },
    "ui:style": {
      fontSize: "14px",
    },
    architectural_landmarks: { 
      "ui:emptyValue": "",
      items: {
        alias: {
          "ui:widget": "typeahead"
        }
      }
    },
  };

  const formData = processedData;

  // Handle form changes
  const handleFormChange = (data) => {
    setProcessedData(data.formData);
  };

  // Fetch initial data
  useEffect(() => {
    const getPrefaceData = async () => {
      try {
        if (alias) {
          const result = await getLocationData(alias, 'introduction', 'architectural-landmarks');
          if (result) {
            setProcessedData(transFormData(result?.[0]));
            setBasicFieldData(transFormData(result?.[0]));
          }
        }
      } catch (error) {
        console.error('Error fetching film data:', error);
                showErrorToast(toast, "Failed to fetch film data");
      } finally {
        setIsLoading(false);
      }
    };

    getPrefaceData();
  }, [alias, toast, quickSave]);

  const transFormData = (data) => ({
    ...data,
    certificate_image_alias: data?.certificate_image_alias?.[0],
  });

  const reTransFormData = (data) => ({
    ...data,
    certificate_image_alias: [data?.certificate_image_alias],
  });

  // Submit updated data
  const handleSubmit = async (formData) => {
    const originalFormattedData = reTransFormData(formData);
    const deletedValues = getDeletedValues(basicFieldData, originalFormattedData);

    try {
      if (alias) {
        setIsSubmitting(true);
        const result = await updateLocationData(alias, originalFormattedData, deletedValues);
        if (result?.status === 200) {
          setQuickSave(prev => !prev);
                    showSuccessToast(toast, "Data updated successfully");
        }
      }
    } catch (error) {
      console.error('Error updating film data:', error);
            showErrorToast(toast, "Failed to update film data");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box maxW="auto" pb="50px">
      {isLoading ? (
        <FormLoader />
      ) : (
        <Box bg="white" border="1px solid" borderColor="gray.200" p={4} borderRadius="md">
          <Form
            schema={schema}
            uiSchema={uiSchema}
            formData={formData}
            validator={validator}
            widgets={widgets}
            onChange={handleFormChange}
          />
        </Box>
      )}
      
      {/* Fixed Save Button at Bottom */}
      <Box
        position="fixed"
        bottom={0}
        left="250px" // Account for sidebar width
        right={0}
        bg="white"
        borderTop="1px solid"
        borderColor="gray.200"
        p={4}
        zIndex={1000}
        display="flex"
      >
        <Box maxW="auto" display="flex" mx="auto">
          <Button
            colorScheme="blue"
            size="lg"
            width="auto"
            isLoading={isSubmitting}
            loadingText="Saving..."
            onClick={() => {
              if (formData) {
                handleSubmit(formData);
              }
            }}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default ArchitecturalLandmarks;